import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Plus, Loader2, Edit3, Trash2, Save, School as SchoolIcon, MapPin, Check } from "lucide-react";
import { api } from "../lib/api";
import useEscClose from "../hooks/useEscClose";
import { confirmDialog } from "../utils/dialogs";

/**
 * SchoolsManager — controlled-vocabulary master for the "School / Academy"
 * baseline field.
 *
 * Why a master rather than free text? Coaches typed "St. Mark's Hyderabad",
 * "St Marks Hyd", "st. marks" over the years and the by-school filter
 * pills ended up with three ghost rows for the same school. This modal is
 * the single place to add / rename / retire schools; rename cascades onto
 * every athlete's baseline server-side so filters stay clean.
 *
 * Admin only. Sits inside the UserMenu.
 */
export default function SchoolsManager({ open, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", city: "", notes: "" });
  const [showInactive, setShowInactive] = useState(false);

  useEscClose(open, onClose);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get(`/schools?include_inactive=${showInactive}`);
      setItems(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load schools");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (open) load(); }, [open, showInactive]);

  const startAdd = () => {
    setEditingId(null);
    setForm({ name: "", city: "", notes: "" });
    setShowAdd(true);
    setError("");
  };
  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, city: s.city || "", notes: s.notes || "" });
    setShowAdd(true);
    setError("");
  };
  const cancel = () => { setShowAdd(false); setEditingId(null); setError(""); };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { setError("School name is required"); return; }
    try {
      if (editingId) {
        await api.put(`/schools/${editingId}`, {
          name, city: form.city || null, notes: form.notes || null,
        });
      } else {
        await api.post("/schools", {
          name, city: form.city || null, notes: form.notes || null,
        });
      }
      setShowAdd(false); setEditingId(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    }
  };

  const retire = async (s) => {
    // Two flavours — see backend comment on DELETE /schools/{id}.
    // Synthetic (athlete-derived) schools clear the field from every
    // athlete using it; master schools soft-retire.
    const msg = s.synthetic
      ? `"${s.name}" was typed directly on one or more athlete baselines and is not in the master list. Deleting it will REMOVE this school from every athlete using it. Continue?`
      : `Retire "${s.name}"? Athletes keep this school on their baseline, but it will no longer show up in the dropdown or filter pills.`;
    if (!(await confirmDialog(msg, { confirmLabel: s.synthetic ? "Delete" : "Retire", danger: true }))) return;
    try {
      await api.delete(`/schools/${s.id}`);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Retire failed");
    }
  };

  const reactivate = async (s) => {
    try {
      await api.put(`/schools/${s.id}`, { active: true });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Reactivate failed");
    }
  };

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto py-8"
      style={{ backdropFilter: "blur(6px)", background: "rgba(11,22,39,0.72)" }}
      onClick={onClose}
      data-testid="schools-manager"
    >
      <div className="card w-full max-w-3xl mx-4 p-5 md:p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-slate-100" data-testid="schools-close">
          <X size={20} />
        </button>

        <div className="flex items-center gap-3">
          <div className="rounded-lg p-2 bg-cyan-500/15 border border-cyan-500/40 text-cyan-300">
            <SchoolIcon size={20} />
          </div>
          <div>
            <div className="font-display text-xl text-slate-100">Schools master</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Controlled vocabulary for the &ldquo;School / Academy&rdquo; baseline field.
              Rename here → propagates to every athlete automatically.
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="mt-4 flex items-center gap-2">
          <button onClick={startAdd} className="btn-primary text-[12px]" data-testid="schools-add-btn">
            <Plus size={13} /> Add school
          </button>
          <label className="text-[11px] text-slate-400 flex items-center gap-1.5 ml-auto cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)}
                   className="accent-cyan-400" data-testid="schools-toggle-inactive" />
            Show retired
          </label>
        </div>

        {error && (
          <div className="mt-3 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-[12px]">{error}</div>
        )}

        {/* Add/Edit inline form */}
        {showAdd && (
          <div className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-500/5 p-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Name *</div>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="form-input text-[12px]" placeholder="e.g. St. Mark's School" autoFocus
                       data-testid="schools-form-name" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">City</div>
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                       className="form-input text-[12px]" placeholder="e.g. Hyderabad"
                       data-testid="schools-form-city" />
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Notes</div>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                       className="form-input text-[12px]" placeholder="e.g. Sports quota partner since 2022"
                       data-testid="schools-form-notes" />
              </div>
            </div>
            <div className="mt-3 flex gap-2 justify-end">
              <button onClick={cancel} className="btn-ghost text-[12px]" data-testid="schools-form-cancel">Cancel</button>
              <button onClick={save} className="btn-primary text-[12px]" data-testid="schools-form-save">
                <Save size={12} /> {editingId ? "Update" : "Add"}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="mt-4 rounded-lg border border-[#1E304B] max-h-[52vh] overflow-y-auto">
          {loading ? (
            <div className="py-8 text-center text-slate-400 text-[12px] flex items-center justify-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="py-8 text-center text-slate-500 text-[11px]">
              No schools yet. Click &ldquo;Add school&rdquo; to create the first one.
            </div>
          ) : (
            <ul>
              {items.map((s) => (
                <li key={s.id}
                    className={`border-b border-[#1E304B] last:border-b-0 px-3 py-2 flex items-center gap-2 ${s.active === false ? "opacity-50" : ""}`}
                    data-testid={`schools-row-${s.id}`}>
                  <SchoolIcon size={14} className="text-cyan-300 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] text-slate-100 font-mono-data flex items-center gap-2">
                      {s.name}
                      {s.active === false && (
                        <span className="text-[9px] uppercase tracking-widest px-1 rounded bg-slate-600/40 text-slate-300">retired</span>
                      )}
                    </div>
                    {(s.city || s.notes) && (
                      <div className="text-[10px] text-slate-500 flex items-center gap-3 mt-0.5">
                        {s.city && <span className="flex items-center gap-1"><MapPin size={9} /> {s.city}</span>}
                        {s.notes && <span className="truncate">{s.notes}</span>}
                      </div>
                    )}
                  </div>
                  <button onClick={() => startEdit(s)} className="btn-ghost !p-1.5" title="Edit"
                          data-testid={`schools-edit-${s.id}`}>
                    <Edit3 size={12} />
                  </button>
                  {s.active === false ? (
                    <button onClick={() => reactivate(s)} className="btn-ghost !p-1.5 !text-emerald-300" title="Reactivate"
                            data-testid={`schools-reactivate-${s.id}`}>
                      <Check size={12} />
                    </button>
                  ) : (
                    <button onClick={() => retire(s)} className="btn-ghost !p-1.5 !text-rose-300" title="Retire"
                            data-testid={`schools-retire-${s.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
